/**
 * The website as a home for your own reminders — the owner's request of
 * 4 October 2026.
 *
 * care.drbell.app was a caregiver's window: somebody who signed up here
 * with no record was told "Nobody to show yet… use the DrBell app on your
 * phone". Now they can set up their own reminders here, or a record for
 * somebody they look after, and keep it — routine, medicines at each time,
 * language, time zone, look — as on the phone.
 *
 * The rules the screens follow, free of React, so they are tested on paper
 * (test/self-service.test.ts). Each mirrors the phone's, which is named.
 */
import { appStoreUrl, localDateAt, zoneOffsetLabel, type MealAnchor } from '@drbell/core';
import type { MealTimes, PatientSummary, RoutineTime, Tier } from './api.ts';

// ── Who is DrBell for (the phone's Onboarding) ────────────────────────────

export type Who = 'me' | 'other';

/**
 * Which set-ups to offer. The phone asks "Who is DrBell for?" — Me, or
 * Someone I look after — and once the account has its own record, adding
 * another person skips the question: it is somebody else
 * (App.tsx, AddPerson: `initialWho`). One person has one own record.
 */
export function setupChoices(patients: readonly Pick<PatientSummary, 'isSelf'>[]): readonly Who[] {
  return patients.some((p) => p.isSelf) ? ['other'] : ['me', 'other'];
}

/**
 * The name somebody chose, or nothing (the phone's lib/chosen-name.ts).
 * Signing up by phone stores the number as the account's name; that is not
 * a name anybody gave, so "Your name" starts empty rather than "+91…".
 */
export function chosenName(name: string | null | undefined, phone?: string | null): string | null {
  const trimmed = name?.trim() ?? '';
  if (!trimmed) return null;
  const digits = (v: string) => v.replace(/\D/g, '');
  if (phone && digits(trimmed) === digits(phone) && digits(phone).length > 0) return null;
  if (/^\+?[\d\s()-]{6,}$/.test(trimmed)) return null;
  return trimmed;
}

/**
 * The record's presentation, as offered (the phone's `modeAvailable`): a
 * mode switched off in the control plane is not offered — except the one a
 * record already has, so opening its settings never silently changes it.
 */
export type Mode = 'elder' | 'standard' | 'kid';
export function modesOffered(flags: Record<string, unknown>, current?: Mode): Mode[] {
  const on = (m: Mode) =>
    m === 'elder' ? flags['ui.elder_mode'] !== false : m === 'kid' ? flags['ui.kid_mode'] !== false : true;
  return (['elder', 'standard', 'kid'] as const).filter((m) => on(m) || m === current);
}

export const MODE_TEXT: Record<Mode, { title: string; body: string }> = {
  elder: {
    title: 'Large and simple',
    // What it does today (the owner, 4 Oct 2026): the phone's catalogue string routine.mode_elder_body.
    body: 'Large words and big buttons on the person’s own phone: the same screens as Standard, only bigger. The size can still be changed on that phone, under Text size.',
  },
  standard: { title: 'Standard', body: 'Ordinary text size. For most adults.' },
  kid: { title: 'For a child', body: 'Stickers and a streak. A grown-up confirms each dose the child reports.' },
};

// ── Who comes first ────────────────────────────────────────────────────────

/**
 * The person's own record first ("You"), then the people they help, in the
 * order the server gave. The record shown when nothing was chosen is the
 * first of these.
 */
export function orderPeople<P extends Pick<PatientSummary, 'id' | 'isSelf'>>(patients: readonly P[]): P[] {
  return [...patients.filter((p) => p.isSelf), ...patients.filter((p) => !p.isSelf)];
}

// ── Meal times (the phone's routine-rules.ts) ──────────────────────────────

export const MEALS = ['breakfast', 'lunch', 'dinner', 'bedtime'] as const;
export type Meal = (typeof MEALS)[number];
export const MEAL_NAME: Record<Meal, string> = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', bedtime: 'Bedtime' };

const minutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
};

/** A warning, never a block: a night shift genuinely eats "breakfast" at 21:00. */
export function outOfOrder(times: MealTimes): boolean {
  const order = MEALS.map((meal) => minutes(times[meal]));
  return order.some((t, i) => i > 0 && t <= order[i - 1]!);
}

export const sameTimes = (a: MealTimes, b: MealTimes) => MEALS.every((m) => a[m] === b[m]);

/** "08:00" as people read it: "8:00 am". */
export function spokenTime(hhmm: string): string {
  const m = minutes(hhmm);
  return new Date(2000, 0, 1, Math.floor(m / 60), m % 60).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
}

/** The person's own calendar day: what "from today" means for their medicines. */
export const todayIn = (timeZone: string, now = Date.now()) => localDateAt(now, timeZone);

// ── Saying a link back (the phone's RoutineLinks and ParsePreview) ─────────

/** A time's name in a sentence: a meal in lower case, an own time exactly as typed. */
export function timeInSentence(key: string, own: readonly RoutineTime[]): string {
  if (key.startsWith('time:')) return own.find((t) => `time:${t.id}` === key)?.name ?? 'that time';
  return (MEAL_NAME[key as Meal] ?? key).toLowerCase();
}

/** "1 — 30 min after lunch", "1 — at bedtime": one anchor, said. */
export function anchorLine(anchor: MealAnchor, own: readonly RoutineTime[]): string {
  const qty = String(anchor.doseQuantity ?? 1);
  const meal = timeInSentence(anchor.meal, own);
  if (anchor.offsetMinutes === 0) return `${qty} — at ${meal}`;
  return anchor.offsetMinutes > 0
    ? `${qty} — ${anchor.offsetMinutes} min after ${meal}`
    : `${qty} — ${Math.abs(anchor.offsetMinutes)} min before ${meal}`;
}

/** "Dolo 650 will be taken 3 times a day instead of 2." — or that the number stays. */
export function countLine(medicine: string, before: number, after: number): string {
  if (before === after) return 'It is still taken the same number of times a day.';
  return after === 1
    ? `${medicine} will be taken once a day instead of ${before} times.`
    : `${medicine} will be taken ${after} times a day instead of ${before}.`;
}

// ── Time zones (the phone's TimezonePicker) ────────────────────────────────

const COMMON_ZONES = [
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Qatar', 'Asia/Kuwait', 'Asia/Muscat', 'Asia/Bahrain',
  'Asia/Karachi', 'Asia/Kathmandu', 'Asia/Dhaka', 'Asia/Colombo', 'Asia/Singapore', 'Asia/Kuala_Lumpur',
  'Asia/Bangkok', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Australia/Perth', 'Australia/Sydney', 'Pacific/Auckland',
  'Europe/London', 'Europe/Dublin', 'Europe/Paris', 'Europe/Berlin', 'Europe/Amsterdam', 'Africa/Johannesburg',
  'Africa/Nairobi', 'Africa/Lagos', 'America/Sao_Paulo', 'America/New_York', 'America/Toronto', 'America/Chicago',
  'America/Denver', 'America/Los_Angeles', 'America/Vancouver',
];

/** Old names some browsers still report, for the zone everybody else calls by its current one. */
const ZONE_ALIASES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata', 'Asia/Katmandu': 'Asia/Kathmandu', 'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Rangoon': 'Asia/Yangon', 'Europe/Kiev': 'Europe/Kyiv',
};

/** This browser's zone, by its current name: "Asia/Kolkata", never a second entry "Asia/Calcutta" beside it. */
export function browserZone(reported = Intl.DateTimeFormat().resolvedOptions().timeZone): string {
  return ZONE_ALIASES[reported] ?? (reported || 'Asia/Kolkata');
}

export const zoneName =(zone: string, at = Date.now()) => `${zone.replace(/_/g, ' ')} (${zoneOffsetLabel(zone, at)})`;

/** The current zone and this browser's, then the places DrBell's families are, ordered by offset now. */
export function zoneChoices(current: string, browser: string, at = Date.now()): string[] {
  const offset = (zone: string) => {
    const label = zoneOffsetLabel(zone, at);
    const sign = label[3] === '-' ? -1 : 1;
    return sign * (Number(label.slice(4, 6)) * 60 + Number(label.slice(7, 9)));
  };
  return [...new Set([current, browser, ...COMMON_ZONES])].filter(Boolean).sort((a, b) => offset(a) - offset(b));
}

// ── The plan (the phone's Profile → Your plan) ─────────────────────────────

export const TIER_NAME: Record<Tier, string> = {
  free: 'Free', plus: 'DrBell Plus', family: 'DrBell Family', org: 'Care home',
};

/**
 * Where a plan came from, in words (the console's `sourceOf`): the store
 * that bills it, a gift from DrBell, or an organisation. Null on the free
 * plan, which nobody bought.
 */
export function planSource(tier: Tier, store: string | null | undefined): string | null {
  if (tier === 'free') return null;
  if (tier === 'org') return 'Through a care home or organisation';
  if (store === 'google') return 'Bought on Google Play';
  if (store === 'apple') return 'Bought on the App Store';
  if (store === 'complimentary') return 'A gift from DrBell';
  if (store === 'razorpay' || store === 'stripe') return 'Bought on this website';
  if (!store || store === 'manual') return 'Set up for you by DrBell';
  return 'Paid online';
}

/** Who to see about billing: the store that takes the money cancels it too. */
export function managedWhere(store: string | null | undefined): string | null {
  if (store === 'google') return 'Billed by Google Play, and cancelled there too (Play Store → Payments and subscriptions). Cancelling leaves you on the free plan with every reminder still working.';
  if (store === 'apple') return 'Billed by the App Store, and cancelled there too (Settings → your name → Subscriptions). Cancelling leaves you on the free plan with every reminder still working.';
  if (store === 'razorpay' || store === 'stripe') return 'Bought on this website, and managed or cancelled here too (Manage your plan). Cancelling leaves you on the free plan with every reminder still working.';
  return null;
}

// ── Where reminders ring ───────────────────────────────────────────────────

/** The Android app, on Google Play. */
export const PLAY_URL = 'https://play.google.com/store/apps/details?id=app.drbell.mobile';

/** The iPhone app, on the App Store. */
export const APP_STORE_URL = appStoreUrl({ campaign: 'care' });
